import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { PDF_TEMPLATES } from '../QDFConstants';
import { formatDateTime, generatePDFFileName } from '../utils/qdfUtils';

const INSPECTION_LAYOUT = {
  // Operation table
  remarksX: 332,
  remarksWidth: 225,
  statusBoxX: 560,
  firstRowY: 495,
  rowStep: 15.2,
  rowYPositions: [495, 475, 460, 444.8, 429.5, 414.5, 399.3, 384.3, 369, 354, 338.7, 318.7, 303.4, 288.4],

  // Spare parts section
  spareDeviceX: 180,
  spareDescX: 180,
  spareQtyX: 180,
  spareWidth: 360,
  spareDeviceY: 247,
  spareDescY: 235,
  spareQtyY: 222,

  // Remarks + prepared by
  commentsX: 180,
  commentsY: 180,
  commentsWidth: 360,
  preparedByX: 433,
  preparedByY: 123.3,
};

const SR_COMPLETION_LAYOUT = {
  // Spare parts section (corrective completion modal data)
  spareDeviceX: 180,
  spareDescX: 180,
  spareQtyX: 180,
  spareWidth: 360,
  spareDeviceY: 424,
  spareDescY: 411,
  spareQtyY: 398,
  spareFontSize: 10,
  qtyFontSize: 10,

  // Remarks section (corrective completion modal data)
  commentsX: 180,
  commentsY: 350,
  commentsWidth: 360,
  commentsFontSize: 10,

  // Prepared by
  preparedByX: 464,
  preparedByY: 274.6,
  preparedByFontSize: 10.5,
};

const getChecklistRowY = (layout, idx) => {
  const mappedY = layout?.rowYPositions?.[idx];
  if (Number.isFinite(mappedY)) return mappedY;
  return layout.firstRowY - idx * layout.rowStep;
};

const drawWrappedText = ({ page, font, text, x, y, maxWidth, size = 8, lineHeight = 9, maxLines = 2 }) => {
  if (!text) return;
  const words = String(text).trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return;

  const lines = [];
  let current = '';

  words.forEach((word) => {
    const test = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(test, size) <= maxWidth) {
      current = test;
      return;
    }
    if (current) lines.push(current);
    current = word;
  });
  if (current) lines.push(current);

  lines.slice(0, maxLines).forEach((line, idx) => {
    page.drawText(line, {
      x,
      y: y - idx * lineHeight,
      size,
      font,
      color: rgb(0, 0, 0),
    });
  });
};

const drawMultiline = ({ page, font, text, x, y, maxWidth, size = 8, lineHeight = 9, maxLines = 2 }) => {
  const rows = Array.isArray(text)
    ? text.filter(Boolean)
    : String(text || '')
      .split(/\n/)
      .map((t) => t.trim())
      .filter(Boolean);

  if (rows.length === 0) return;

  let line = 0;
  rows.forEach((row) => {
    if (line >= maxLines) return;
    const words = row.split(/\s+/).filter(Boolean);
    let current = '';

    words.forEach((word) => {
      if (line >= maxLines) return;
      const test = current ? `${current} ${word}` : word;
      if (font.widthOfTextAtSize(test, size) <= maxWidth) {
        current = test;
      } else {
        if (current) {
          page.drawText(current, {
            x,
            y: y - line * lineHeight,
            size,
            font,
            color: rgb(0, 0, 0),
          });
          line += 1;
        }
        current = word;
      }
    });

    if (line < maxLines && current) {
      page.drawText(current, {
        x,
        y: y - line * lineHeight,
        size,
        font,
        color: rgb(0, 0, 0),
      });
      line += 1;
    }
  });
};

const fillCommonTemplateFields = ({ formData, maintenanceType, page, height, font }) => {
  const drawText = (text, x, y, size = 10) => {
    page.drawText(text || '', {
      x,
      y,
      size,
      font,
      color: rgb(0, 0, 0),
    });
  };

  // Draw common fields
  drawText(formData.weekNumber, 212, height - 112);
  drawText(formData.scNumber, 182, height - 125);
  drawText(formData.scStatus, 444, height - 125);
  drawText(formatDateTime(formData.creationDate), 180, height - 138);
  drawText(formData.contractor, 444, height - 138);
  drawText(formData.priority, 182, height - 150);
  drawText(formData.scDescription, 182, height - 163);
  drawText(formatDateTime(formData.targetResponse), 182, height - 176);
  drawText(formatDateTime(formData.targetResolution), 444, height - 176);

  // Only draw actual dates for corrective maintenance
  if (maintenanceType === 'corrective') {
    drawText(formatDateTime(formData.actualDate), 182, height - 189);
    drawText(formatDateTime(formData.actualResolution), 444, height - 189);
  }

  // Conditional asset details based on maintenance type and PPM type
  if (maintenanceType === 'preventive' && formData.ppmType === 'Monthly') {
    // Monthly PPM - only location and zone fields with different coordinates
    drawText(formData.location, 180, height - 229);
    drawText(formData.zone, 180, height - 242);
  } else {
    // All other types - full asset details
    drawText(formData.assetNumber, 180, height - 231);
    drawText(formData.assetDescription, 180, height - 244);
    drawText(formData.department, 180, height - 258);
    drawText(formData.assetTag, 360, height - 258);
    drawText(formData.location, 180, height - 272);
  }

  // Add requester details for corrective maintenance
  if (maintenanceType === 'corrective') {
    drawText(formData.requesterName, 180, height - 314);
    drawText(formData.requesterContact || formData.contactNumber, 180, height - 328);
  }
};

const fillInspectionFields = ({ formData, maintenanceType, page, font }) => {
  if (maintenanceType !== 'preventive') return;

  const inspection = formData.inspection;
  if (!inspection) return;

  const items = Array.isArray(inspection.items) ? inspection.items : [];

  // Mark checklist rows: remarks + status indicator + crossed status box.
  items.forEach((item, idx) => {
    const y = getChecklistRowY(INSPECTION_LAYOUT, idx);

    drawWrappedText({
      page,
      font,
      text: item?.remarks || '',
      x: INSPECTION_LAYOUT.remarksX,
      y,
      maxWidth: INSPECTION_LAYOUT.remarksWidth,
      size: 7,
      lineHeight: 8,
      maxLines: 2,
    });

    const status = item?.status;
    if (status) {
      page.drawText('X', {
        x: INSPECTION_LAYOUT.statusBoxX,
        y,
        size: 9,
        font,
        color: rgb(0, 0, 0),
      });
    }
  });

  const spareParts = Array.isArray(inspection.spareParts) ? inspection.spareParts : [];

  const deviceLines = spareParts
    .map((p) => p?.deviceName)
    .filter(Boolean);

  const descLines = spareParts
    .map((p) => {
      const description = p?.description || '';
      const remarks = p?.remarks || '';
      if (description && remarks) return `${description} (${remarks})`;
      return description || remarks;
    })
    .filter(Boolean);

  const qtyLines = spareParts
    .map((p) => (p?.quantity != null && String(p.quantity).trim() !== '' ? String(p.quantity) : ''))
    .filter(Boolean);

  drawMultiline({
    page,
    font,
    text: deviceLines,
    x: INSPECTION_LAYOUT.spareDeviceX,
    y: INSPECTION_LAYOUT.spareDeviceY,
    maxWidth: INSPECTION_LAYOUT.spareWidth,
    size: 7,
    lineHeight: 8,
    maxLines: 2,
  });

  drawMultiline({
    page,
    font,
    text: descLines,
    x: INSPECTION_LAYOUT.spareDescX,
    y: INSPECTION_LAYOUT.spareDescY,
    maxWidth: INSPECTION_LAYOUT.spareWidth,
    size: 7,
    lineHeight: 8,
    maxLines: 2,
  });

  drawMultiline({
    page,
    font,
    text: qtyLines,
    x: INSPECTION_LAYOUT.spareQtyX,
    y: INSPECTION_LAYOUT.spareQtyY,
    maxWidth: INSPECTION_LAYOUT.spareWidth,
    size: 8,
    lineHeight: 8,
    maxLines: 2,
  });

  drawMultiline({
    page,
    font,
    text: inspection.comments || '',
    x: INSPECTION_LAYOUT.commentsX,
    y: INSPECTION_LAYOUT.commentsY,
    maxWidth: INSPECTION_LAYOUT.commentsWidth,
    size: 8,
    lineHeight: 9,
    maxLines: 3,
  });

  if (inspection.preparedBy) {
    page.drawText(String(inspection.preparedBy), {
      x: INSPECTION_LAYOUT.preparedByX,
      y: INSPECTION_LAYOUT.preparedByY,
      size: 10,
      font,
      color: rgb(0, 0, 0),
    });
  }
};

const fillServiceRequestCompletionFields = ({ formData, maintenanceType, page, font }) => {
  if (maintenanceType !== 'corrective') return;

  const inspection = formData.inspection;
  if (!inspection) return;

  const spareParts = Array.isArray(inspection.spareParts) ? inspection.spareParts : [];

  const deviceLines = spareParts
    .map((p) => p?.deviceName)
    .filter(Boolean);

  const descLines = spareParts
    .map((p) => p?.description || '')
    .filter(Boolean);

  const qtyLines = spareParts
    .map((p) => (p?.quantity != null && String(p.quantity).trim() !== '' ? String(p.quantity) : ''))
    .filter(Boolean);

  drawMultiline({
    page,
    font,
    text: deviceLines,
    x: SR_COMPLETION_LAYOUT.spareDeviceX,
    y: SR_COMPLETION_LAYOUT.spareDeviceY,
    maxWidth: SR_COMPLETION_LAYOUT.spareWidth,
    size: SR_COMPLETION_LAYOUT.spareFontSize,
    lineHeight: 8,
    maxLines: 2,
  });

  drawMultiline({
    page,
    font,
    text: descLines,
    x: SR_COMPLETION_LAYOUT.spareDescX,
    y: SR_COMPLETION_LAYOUT.spareDescY,
    maxWidth: SR_COMPLETION_LAYOUT.spareWidth,
    size: SR_COMPLETION_LAYOUT.spareFontSize,
    lineHeight: 8,
    maxLines: 2,
  });

  drawMultiline({
    page,
    font,
    text: qtyLines,
    x: SR_COMPLETION_LAYOUT.spareQtyX,
    y: SR_COMPLETION_LAYOUT.spareQtyY,
    maxWidth: SR_COMPLETION_LAYOUT.spareWidth,
    size: SR_COMPLETION_LAYOUT.qtyFontSize,
    lineHeight: 8,
    maxLines: 2,
  });

  drawMultiline({
    page,
    font,
    text: inspection.comments || '',
    x: SR_COMPLETION_LAYOUT.commentsX,
    y: SR_COMPLETION_LAYOUT.commentsY,
    maxWidth: SR_COMPLETION_LAYOUT.commentsWidth,
    size: SR_COMPLETION_LAYOUT.commentsFontSize,
    lineHeight: 9,
    maxLines: 3,
  });

  if (inspection.preparedBy) {
    page.drawText(String(inspection.preparedBy), {
      x: SR_COMPLETION_LAYOUT.preparedByX,
      y: SR_COMPLETION_LAYOUT.preparedByY,
      size: SR_COMPLETION_LAYOUT.preparedByFontSize,
      font,
      color: rgb(0, 0, 0),
    });
  }
};

const buildPopulatedPdf = async (formData, maintenanceType) => {
  // Determine which template to use based on maintenance type
  let pdfPath;
  if (maintenanceType === 'preventive') {
    if (!formData.ppmType) {
      throw new Error('Please select a PPM Type.');
    }
    pdfPath = PDF_TEMPLATES[formData.ppmType];
    if (!pdfPath) {
      throw new Error('Invalid PPM Type selected.');
    }
  } else {
    // Corrective maintenance
    pdfPath = PDF_TEMPLATES.Corrective;
  }

  const existingPdfBytes = await fetch(pdfPath).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const page = pdfDoc.getPages()[0];
  const { height } = page.getSize();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  fillCommonTemplateFields({ formData, maintenanceType, page, height, font });
  fillInspectionFields({ formData, maintenanceType, page, font });
  fillServiceRequestCompletionFields({ formData, maintenanceType, page, font });

  return pdfDoc.save();
};

export const generatePDF = async (formData, maintenanceType) => {
  const pdfBytes = await buildPopulatedPdf(formData, maintenanceType);

  // Create download
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = generatePDFFileName(formData, maintenanceType);
  link.click();
};

// New function to generate PDF bytes without downloading
export const generatePDFBytes = async (formData, maintenanceType) => {
  const pdfBytes = await buildPopulatedPdf(formData, maintenanceType);
  const fileName = generatePDFFileName(formData, maintenanceType);

  return { pdfBytes, fileName };
};
